import { useMemo, useState } from "react";
import { useForm } from "@tellescope/forms";
import {
  Button,
  Combobox,
  ComboboxOption,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  Label,
  Textarea,
  withKey,
} from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import {
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import { formatName } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

type Enduser = TellescopeModelFromName<"endusers">;

export interface BookingNoteFormDialogContentsProps {
  bookingPageId: string;
  bookingPageTitle?: string;
  onSave?: (enduserId: string) => void;
  onCancel?: () => void;
  onError?: () => void;
}

export const BookingNoteFormDialogContents = withKey(
  ({ bookingPageId, bookingPageTitle, onSave, onCancel, onError }: BookingNoteFormDialogContentsProps) => {
    const { t } = useTranslation("calendar");
    const [enduserSearch, setEnduserSearch] = useState("");
    const [debouncedEnduserSearch] = useDebouncedValue(enduserSearch);
    const [selectedEnduser, setSelectedEnduser] = useState<Enduser>();

    const bookingPageQuery = useTellescopeQuery({
      model: "appointment_booking_pages",
      method: "getOne",
      request: bookingPageId,
      enabled: !bookingPageTitle,
      select: (data) => data.title,
    });

    const { data: endusers = [], isLoading } = useTellescopeInfiniteQuery({
      model: "endusers",
      method: "getSome",
      request: {
        search: debouncedEnduserSearch ? { query: debouncedEnduserSearch } : undefined,
      },
      select: (data) => data.pages.flat(),
    });

    const updateEnduserMutation = useTellescopeMutation({
      model: "endusers",
      method: "updateOne",
      onSuccess: ({ id }) => onSave?.(id),
      onError: () => onError?.(),
    });

    const form = useForm({
      fields: {
        enduserId: {
          type: "text",
          initialValue: "",
          required: true,
        },
        note: {
          type: "textarea",
          initialValue: "",
          required: true,
        },
      },
      validators: {
        note: (value) => (value.trim() ? undefined : t("This field is required")),
      },
      onSubmit: async ({ enduserId, note }) => {
        await updateEnduserMutation.mutateAsync({
          id: enduserId,
          bookingNotes: [{ bookingPageId, note: note.trim() }],
        });
      },
    });

    const enduserOptions = useMemo<ComboboxOption<Enduser>[]>(
      () =>
        endusers.map((enduser) => ({
          value: enduser.id,
          label: formatName(enduser) || enduser.id,
          metadata: { enduser },
        })),
      [endusers]
    );

    const selectedOption = useMemo<ComboboxOption<Enduser> | undefined>(
      () =>
        selectedEnduser
          ? {
              value: selectedEnduser.id,
              label: formatName(selectedEnduser) || selectedEnduser.id,
              metadata: { enduser: selectedEnduser },
            }
          : undefined,
      [selectedEnduser]
    );

    return (
      <DialogContent divided data-testid="booking-note-form-dialog">
        <DialogHeader className="gap-1">
          {t("Create Patient Booking Note")}

          <DialogDescription>
            {t("For booking page: {{bookingPageTitle}}", {
              bookingPageTitle: bookingPageTitle || bookingPageQuery.data,
            })}
          </DialogDescription>
        </DialogHeader>
        <form {...form.getFormProps()} className="space-y-6 pb-6 px-6 pt-3">
          <div className="space-y-2">
            <Label required htmlFor="booking-note-enduser">
              {t("Patient")}
            </Label>
            <Combobox
              id="booking-note-enduser"
              value={selectedOption}
              onChange={(option) => {
                const enduser = option?.metadata?.enduser;
                setSelectedEnduser(enduser);
                form.setFieldValue("enduserId", enduser?.id ?? "");
              }}
              options={enduserOptions}
              alwaysFilter={false}
              customFilter={(_, options) => options}
              searchQuery={enduserSearch}
              setSearchQuery={setEnduserSearch}
              isLoading={isLoading || enduserSearch !== debouncedEnduserSearch}
              placeholder={t("Select a patient...")}
              searchPlaceholder={t("Search patients...")}
              emptyText={t("No patients found")}
              className="w-full"
              data-testid="booking-note-enduser-select"
            />
          </div>
          <div className="space-y-2">
            <Label required htmlFor="booking-note">
              {t("Note")}
            </Label>
            <Textarea
              {...form.getFieldProps("note")}
              id="booking-note"
              placeholder={t("Enter booking-specific notes for this patient...")}
              data-testid="booking-note-input"
            />
            <p className="text-xs font-light text-muted-foreground">
              {t("This note will be associated with the selected patient for this specific booking page.")}
            </p>
          </div>
        </form>
        <DialogFooter className="bg-muted rounded-b-md">
          <Button variant="outline" onClick={onCancel} data-testid="booking-note-cancel">
            {t("Cancel")}
          </Button>
          <Button
            onClick={() => form.submitForm()}
            disabled={!form.isValid}
            loading={updateEnduserMutation.isPending}
            data-testid="booking-note-submit"
          >
            {t("Create Note")}
          </Button>
        </DialogFooter>
      </DialogContent>
    );
  },
  ({ bookingPageId }) => bookingPageId
);
